/**
 * Custom hook for filtering articles
 * Handles search query, theme filtering, tag filtering and pagination
 */

'use client';

import { useEffect, useMemo, useState, useTransition } from 'react';
import type { Article } from '../types';

interface ThemeNode {
  id: string;
  name: string;
  color?: string;
  level?: number;
  children?: ThemeNode[];
  [key: string]: any;
}

const STRIP_HTML_TAGS = /<[^>]*>/g;

const stripHtml = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  return value.replace(STRIP_HTML_TAGS, '');
};

interface UseArticlesFilterOptions {
  articles: Article[];
  pageSize?: number;
  themesTree?: ThemeNode[];
}

export function useArticlesFilter({
  articles,
  pageSize = 12,
  themesTree = [],
}: UseArticlesFilterOptions) {
  const safePageSize = Math.max(1, pageSize);

  // Filter state
  const [query, setQuery] = useState('');
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [themeFilterMode, setThemeFilterMode] = useState<'OR' | 'AND'>('OR');
  const [tagFilterMode, setTagFilterMode] = useState<'OR' | 'AND'>('OR');
  const [stickyOnly, setStickyOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Use transition for non-urgent filter updates
  const [isPending, startTransition] = useTransition();

  const normalizedQuery = query.trim().toLowerCase();

  // Extract themes from articles and build hierarchical tree with parents
  const themes = useMemo(() => {
    // First, extract unique theme IDs from articles
    const themeIdsInArticles = new Set<string>();

    articles.forEach(article => {
      const articleThemes = article.theme ?? [];
      articleThemes.forEach((theme: any) => {
        if (theme?.id) {
          themeIdsInArticles.add(theme.id);
        }
      });
    });

    // If themesTree is provided, build filtered tree with parents
    if (themesTree && themesTree.length > 0) {
      // Helper: find all ancestor IDs for a given theme
      const findAncestors = (
        nodes: ThemeNode[],
        targetId: string,
        ancestors: Set<string> = new Set()
      ): Set<string> => {
        for (const node of nodes) {
          if (node.id === targetId) {
            return ancestors;
          }
          if (node.children && node.children.length > 0) {
            const newAncestors = new Set([...ancestors, node.id]);
            const found = findAncestors(node.children, targetId, newAncestors);
            if (found.size > ancestors.size || found.has(targetId)) {
              return found;
            }
          }
        }
        return ancestors;
      };

      // Collect all theme IDs that should be displayed (themes in articles + their ancestors)
      const allRelevantIds = new Set<string>(themeIdsInArticles);

      themeIdsInArticles.forEach(themeId => {
        const ancestors = findAncestors(themesTree, themeId);
        ancestors.forEach(ancestorId => allRelevantIds.add(ancestorId));
      });

      // Filter the tree to include only relevant themes and propagate colors from parents to children
      const filterTree = (nodes: ThemeNode[], parentColor?: string | null): ThemeNode[] => {
        const result: ThemeNode[] = [];

        for (const node of nodes) {
          if (allRelevantIds.has(node.id)) {
            // Use node color if available and not empty, otherwise inherit from parent
            const nodeColor =
              node.color && node.color.trim() !== ''
                ? node.color
                : parentColor && parentColor.trim() !== ''
                  ? parentColor
                  : undefined;
            const filteredChildren = node.children ? filterTree(node.children, nodeColor) : [];
            result.push({
              ...node,
              color: nodeColor,
              children: filteredChildren,
            });
          }
        }

        return result;
      };

      return filterTree(themesTree);
    }

    // Fallback: extract unique themes from articles (flat list)
    const themeMap = new Map<string, { id: string; name: string }>();

    articles.forEach(article => {
      const articleThemes = article.theme ?? [];
      articleThemes.forEach((theme: any) => {
        if (theme?.id && theme?.name && !themeMap.has(theme.id)) {
          themeMap.set(theme.id, {
            id: theme.id,
            name: theme.name,
          });
        }
      });
    });

    return Array.from(themeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [articles, themesTree]);

  // Extract unique tags from articles
  const tags = useMemo(() => {
    const tagMap = new Map<string, { id: string; name: string }>();

    articles.forEach(article => {
      const articleTags = article.tags ?? [];
      articleTags.forEach((tag: any) => {
        if (tag?.id && tag?.name && !tagMap.has(tag.id)) {
          tagMap.set(tag.id, {
            id: tag.id,
            name: tag.name,
          });
        }
      });
    });

    return Array.from(tagMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [articles]);

  // Filter articles
  const filteredArticles = useMemo(() => {
    const getArticleThemeIds = (article: Article) =>
      (article.theme ?? [])
        .map((theme: any) => theme?.id)
        .filter((id: any): id is string => Boolean(id));

    const getArticleTagIds = (article: Article) =>
      (article.tags ?? [])
        .map((tag: any) => tag?.id)
        .filter((id: any): id is string => Boolean(id));

    return articles.filter(article => {
      // Sticky filter
      if (stickyOnly && !article.sticky) {
        return false;
      }

      // Theme filter - OR (at least one) or AND (all themes)
      if (selectedThemes.length > 0) {
        const articleThemeIds = getArticleThemeIds(article);

        if (themeFilterMode === 'OR') {
          const hasAnyTheme = selectedThemes.some(themeId => articleThemeIds.includes(themeId));
          if (!hasAnyTheme) return false;
        } else {
          const hasAllThemes = selectedThemes.every(themeId => articleThemeIds.includes(themeId));
          if (!hasAllThemes) return false;
        }
      }

      // Tag filter - OR (at least one) or AND (all tags)
      if (selectedTags.length > 0) {
        const articleTagIds = getArticleTagIds(article);

        if (tagFilterMode === 'OR') {
          const hasAnyTag = selectedTags.some(tagId => articleTagIds.includes(tagId));
          if (!hasAnyTag) return false;
        } else {
          const hasAllTags = selectedTags.every(tagId => articleTagIds.includes(tagId));
          if (!hasAllTags) return false;
        }
      }

      // Search query
      if (!normalizedQuery) return true;

      // Search in title
      if (article.title.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in subtitle
      if (article.subtitle?.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      // Search in themes
      if (
        article.theme?.some((theme: any) => theme?.name?.toLowerCase().includes(normalizedQuery))
      ) {
        return true;
      }

      // Search in tags
      if (article.tags?.some((tag: any) => tag?.name?.toLowerCase().includes(normalizedQuery))) {
        return true;
      }

      // Search in author name
      if (
        article.author &&
        typeof article.author === 'object' &&
        article.author !== null &&
        'name' in article.author &&
        typeof article.author.name === 'string' &&
        article.author.name.toLowerCase().includes(normalizedQuery)
      ) {
        return true;
      }

      // Search in body content (strip HTML)
      const bodyText = stripHtml(
        article.body?.summary || article.body?.processed || article.body?.value || ''
      );
      if (bodyText.toLowerCase().includes(normalizedQuery)) {
        return true;
      }

      return false;
    });
  }, [
    articles,
    normalizedQuery,
    selectedThemes,
    selectedTags,
    themeFilterMode,
    tagFilterMode,
    stickyOnly,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / safePageSize));

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    normalizedQuery,
    selectedThemes,
    selectedTags,
    themeFilterMode,
    tagFilterMode,
    stickyOnly,
    safePageSize,
    articles,
  ]);

  // Ensure current page doesn&apos;t exceed total pages
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Paginated articles
  const { visibleArticles, startIndex, endIndex } = useMemo(() => {
    const start = (currentPage - 1) * safePageSize;
    const end = Math.min(start + safePageSize, filteredArticles.length);
    return {
      visibleArticles: filteredArticles.slice(start, end),
      startIndex: start,
      endIndex: end,
    };
  }, [filteredArticles, currentPage, safePageSize]);

  // Actions - wrapped with startTransition for non-urgent updates
  const toggleTheme = (themeId: string) => {
    startTransition(() => {
      setSelectedThemes(prev =>
        prev.includes(themeId) ? prev.filter(id => id !== themeId) : [...prev, themeId]
      );
    });
  };

  const toggleTag = (tagId: string) => {
    startTransition(() => {
      setSelectedTags(prev =>
        prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
      );
    });
  };

  const clearThemeSelection = () => {
    startTransition(() => {
      setSelectedThemes([]);
    });
  };

  const clearTagSelection = () => {
    startTransition(() => {
      setSelectedTags([]);
    });
  };

  const handlePageChange = (page: number) => {
    startTransition(() => {
      setCurrentPage(page);
    });
  };

  const clearAllFilters = () => {
    startTransition(() => {
      setQuery('');
      setSelectedThemes([]);
      setSelectedTags([]);
      setStickyOnly(false);
      setCurrentPage(1);
    });
  };

  // Wrapper for setQuery with transition
  const handleQueryChange = (newQuery: string) => {
    startTransition(() => {
      setQuery(newQuery);
    });
  };

  const totalResults = filteredArticles.length;
  const hasResults =
    totalResults > 0 ||
    normalizedQuery !== '' ||
    selectedThemes.length > 0 ||
    selectedTags.length > 0 ||
    stickyOnly;
  const hasActiveFilters =
    normalizedQuery !== '' || selectedThemes.length > 0 || selectedTags.length > 0 || stickyOnly;

  return {
    query,
    selectedThemes,
    selectedTags,
    themeFilterMode,
    tagFilterMode,
    stickyOnly,
    currentPage,
    themes,
    tags,
    visibleArticles,
    totalResults,
    hasResults,
    hasActiveFilters,
    totalPages,
    startIndex,
    endIndex,
    normalizedQuery,
    isPending,
    setQuery: handleQueryChange,
    toggleTheme,
    toggleTag,
    clearThemeSelection,
    clearTagSelection,
    setThemeFilterMode,
    setTagFilterMode,
    setStickyOnly,
    handlePageChange,
    clearAllFilters,
  };
}
